import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectS3Signature, selectUserId } from '~/user/selectors';
import { selectCurrentOrganizationId } from '~/organizations/selectors';
import { Uploader, UploaderEvents } from '../lib/Uploader';
import { bindUploaderToReduxSlice } from '../state/bindUploaderToReduxSlice';

import { OnFileReadyCb } from '../types';
import {
  destroyUploader,
  getOrCreateUploader,
  resetUploader,
} from '../lib/uploader/UploadersMap';
import { UploaderRestrictions } from '../values/UploaderRestrictions';

/**
 * Returns an uploader instance for the given id with
 * uploader events bound to the redux store.
 * It also returns the current state of the uploader
 * and some callbacks.
 */
export const useUploader = ({
  id,
  fileRestrictions,
  eventHandlers = {},
  // TODO UPLOADS: 'keep' is a better default so that we make reset/destroy explicit
  onUnmount = 'reset',
}: {
  id: string;
  fileRestrictions?: UploaderRestrictions;
  eventHandlers?: Partial<UploaderEvents>;
  onUnmount?: 'destroy' | 'reset' | 'keep';
}): {
  uploader: Uploader;
  onFileReady: OnFileReadyCb;
} => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const s3UploadSignature = useAppSelector(selectS3Signature);

  const uploader = getOrCreateUploader({
    uploaderId: id,
    userId,
    s3UploadSignature,
    fileRestrictions,
    eventHandlers,
  });

  useEffect(() => {
    if (onUnmount === 'reset') {
      return () => {
        resetUploader({ uploaderId: id });
      };
    }
    if (onUnmount === 'destroy') {
      return () => {
        destroyUploader({ uploaderId: id });
      };
    }
  }, [id, onUnmount]);

  bindUploaderToReduxSlice(uploader, { dispatch });

  return useMemo(
    () => ({
      // @fixme: typescript thinks uploader could be undefined
      uploader: uploader as Uploader,

      onFileReady: (file, source) => {
        if (file)
          uploader.addFiles([file], {
            organizationId,
            // TODO UPLOADS use accountId instead of userId
            accountId: userId,
            source,
          });
      },
    }),
    [uploader, organizationId, userId]
  );
};
